import { toUtcDate } from 'ccs-ui-components';
import { SOURCE_REGISTRY_FORM_PARAM, STATUS_TYPES, UTCFILTER } from '../../constants';
import { IPatternDefinition, ISourceRegistryErrorModel, ISourceRegistryValidationModel, SourceRegistryFormData } from '../../models';
import { sourceRegistryNameResponseSchema } from './RegistriesSchemaDefintion';
import { ERRORS_SR } from './RegistryUtils';
import { ISourceRegistryFilterState } from '../../store/reducer/sources/SourceRegistryFilterReducer';
import { ISourceListQueryParams } from '../../modules/sources/source-registry/source-list/SourceList';

/**
 * @method updateFormData
 * @description Updates the form data with the provided error and validation status.
 * @param previousData - The previous form data to be updated.
 * @param error - The error message to be set.
 * @param isValidStatus - The validation status to be set.
 * @returns The updated form data.
 */
export const updateFormData = (previousData: ISourceRegistryValidationModel, error: string, isValidStatus: boolean) => {
  previousData.sourceRegistryName.error = error;
  previousData.sourceRegistryName.isValid = isValidStatus;
  return previousData;
};

/**
 * @method identifierChecker
* @description method to check error states of identifier input
*/
export const identifierChecker = (formData: ISourceRegistryValidationModel, isDuplicate: string) => {
  let idnErr = '';
  if (!formData.identifiers.isValid && formData.identifiers.isTouched && !isDuplicate) idnErr = formData.identifiers.error ?? '';
  else if (isDuplicate) idnErr = isDuplicate;
  return idnErr;
};

/**
 * @method addBtnStatus
* @description method to check states of add btn identifier input
*/
export const addBtnStatus = (formData: ISourceRegistryValidationModel, isDuplicate: string) => {
  let validity = false;
  if (!formData.identifiers.value || !formData.identifiers.isValid || isDuplicate) {
    validity = true;
  }
  return validity;
};

/**
 * @method validateFormData
 * @description method to toggle the status of create source registry btn
 */
export const validateFormData = ({ formData, isDuplicate, identifiersArray, viewFormData, responseArray, patternList, initialPatternData }
  : { formData: ISourceRegistryValidationModel, isDuplicate: string, identifiersArray: string[], viewFormData: SourceRegistryFormData, responseArray: string[], patternList: IPatternDefinition[], initialPatternData: IPatternDefinition[] }) => {
  if (
    !formData.sourceRegistryName.isValid
    || !formData.sourceRegistryName.value
    || !formData.identifiers.isValid
    || !identifiersArray.length
    || isDuplicate.length
    || !formData.eventName.isValid
    || !formData.requestSpecJson.isValid
    || !formData.replyToTopic.isValid
    || !formData.replyTimeOut.isValid
    || !isFormDataEqual(formData, viewFormData, identifiersArray, responseArray, patternList, initialPatternData)
    || !checkEvent(formData, patternList)
  ) {
    return true;
  }
  return (false);
};

/**
 * @method validateCreateFormData
 * @description Validates the form data for creating a source registry.
 * @param formData - The form data to be validated.
 * @param isDuplicate - The flag indicating if the source registry is a duplicate.
 * @param identifiersArray - The array of identifiers.
 * @param isIdnEqual - The flag indicating if the identifiers are equal.
 * @param patternList - The list of pattern definitions.
 * @returns True if the form data is invalid, false otherwise.
 */
export const validateCreateFormData = (formData: ISourceRegistryValidationModel, isDuplicate: string, identifiersArray: string[], isIdnEqual: boolean, patternList: IPatternDefinition[]) => {
  if (
    !formData.sourceRegistryName.isValid
    || !formData.sourceRegistryName.value
    || !formData.identifiers.isValid
    || !identifiersArray.length
    || isDuplicate.length
    || isIdnEqual
    || (formData.requestSpecJson.value && !formData.requestSpecJson.isValid)
    || (formData.replyToTopic.value && !formData.replyToTopic.isValid)
    || (formData.replyTimeOut.value && !formData.replyTimeOut.isValid)
    || !checkEvent(formData, patternList)
  ) {
    return true;
  }
  return (false);
};

/**
 * @method checkEvent
 * @description Checks if the event is valid based on the provided form data and pattern list.
 * @param formData - The form data containing the event name.
 * @param patternList - The list of pattern definitions.
 * @returns A boolean indicating if the event is valid.
 */
const checkEvent = (formData: SourceRegistryFormData, patternList: IPatternDefinition[]) => {
  if ((formData.eventName.isValid && formData.eventName.value) || (patternList.length > 0)) return true;
  return false;
};

/**
 * @method isFormDataEqual
 * @description Checks if two instances of SourceRegistryFormData are equal.
 * @param formData1 - The first instance of SourceRegistryFormData.
 * @param formData2 - The second instance of SourceRegistryFormData.
 * @param identifiersArray - An array of identifiers.
 * @param responseArray - An array of responses.
 * @returns True if the two instances are equal, false otherwise.
 */
export const isFormDataEqual = (formData1: SourceRegistryFormData, cachedFormData: SourceRegistryFormData, identifiersArray: string[], responseArray: string[], patternList: IPatternDefinition[], initialPatternData: IPatternDefinition[]) => {
  const [eventNameValueWithVersion] = formData1.eventName.value.split(' ');
  if (formData1.replyTimeOut?.value !== cachedFormData?.replyTimeOut?.value) {
    return true;
  }
  if (formData1.replyToTopic?.value !== cachedFormData.replyToTopic?.value) {
    return true;
  }
  if (formatJsonForInput(formData1.requestSpecJson?.value) !== formatJsonForInput(cachedFormData.requestSpecJson?.value)) {
    return true;
  }
  if (eventNameValueWithVersion !== cachedFormData.eventName.value || formData1.eventName.version !== cachedFormData.eventName.version) {
    return true;
  }
  if (!(isEqual([...responseArray], [...identifiersArray]))) {
    return true;
  }
  if (JSON.stringify(patternList) !== JSON.stringify(initialPatternData)) {
    return true;
  }
  return false;
};

/**
 * @method resetFormData
 * @description method to reset the values of formData
 */
export const resetFormData = (formData: ISourceRegistryValidationModel) => {
  const newFormData = { ...formData };
  newFormData.identifiers.value = '';
  newFormData.identifiersArray.value = '';
  newFormData.sourceRegistryName.value = '';
  newFormData.eventName.value = '';
  newFormData.eventName.version = 1;
  newFormData.replyTimeOut.value = '';
  newFormData.replyToTopic.value = '';
  newFormData.requestSpecJson.value = '';
  newFormData.responseSpecJson.value = '';
  return newFormData;
};

/**
 * @method sortArray
* @description method to sort the sequence of items
*/
const sortArray = (item: string[]) => {
  const array = [...item].sort((a, b) => a.localeCompare(b));
  return array;
};

/**
 * @method isEqual
* @description method to check if 2 arrays are equal irrespective of sequence of items
*/
export const isEqual = (responseArray: string[], updatedArray: string[]) => JSON.stringify(sortArray(responseArray)) === JSON.stringify(sortArray(updatedArray));

/**
 * @method validateName
* @description method to validate source name
*/
 
export const validateName = (data: any) => {
  const resValidity = sourceRegistryNameResponseSchema.validate(data?.data?.sources);
  if (resValidity.error) {
    throw new Error(ERRORS_SR.noResponse);
  }
};

/**
 * @method queryParamModifier
 * @description Modifies the query parameters for pagination, filtering, and sorting.
 * @param queryParams - The query parameters to modify.
 * @returns The modified query parameters.
 */
export const queryParamModifier = (queryParams: ISourceListQueryParams) => ({
  page: queryParams.page ? Number(queryParams.page) : 0,
  item: queryParams.item ? Number(queryParams.item) : 10,
  filterData: {
    searchText: queryParams.searchText ?? '',
    fromDate: queryParams.fromDate && queryParams.fromDate !== '' ? `${toUtcDate({ date: queryParams.fromDate }).replace(' ', 'T')}${UTCFILTER.UTC_FROM_TIMESTAMP_EXTENSION_STR}` : null,
    toDate: queryParams.toDate && queryParams.toDate !== '' ? `${toUtcDate({ date: queryParams.toDate }).replace(' ', 'T')}${UTCFILTER.UTC_TO_TIMESTAMP_EXTENSION_STR}` : null,
    status: queryParams.status ? queryParams.status : ''
  },
  sort: queryParams.sort ? `${queryParams.sort},${queryParams.sortType}` : undefined
});

/**
 * @method setFormElementStatus
 * @description Updates the form element status in the source registry validation model.
 * @param previousState - The previous state of the source registry validation model.
 * @param keyName - The key name of the form element.
 * @param value - The new value of the form element.
 * @param error - The error message associated with the form element, if any.
 * @param isValid - Indicates whether the form element is valid.
 * @param isTouched - Indicates whether the form element has been touched.
 * @returns The updated source registry validation model.
 */
export const setFormElementStatus = (previousState: ISourceRegistryValidationModel, keyName: string, value: string, error: string | null, isValid: boolean, isTouched: boolean) => {
  const data = structuredClone(previousState);
  const key = keyName as keyof ISourceRegistryValidationModel;
  data[key].isValid = isValid;
  data[key].value = value;
  data[key].isTouched = isTouched;
  data[key].error = error ?? '';
  return data;
};

/**
 * @method sourceIntentNameSetter
 * @description Sets the source intent name based on the provided parameters.
 * @param dataName - The name of the data.
 * @param previousState - The previous state of the source registry validation model.
 * @param name - The name of the source registry.
 * @param value - The value of the source registry.
 * @param SOURCE_REGISTRY_ERRORS - The error model for source registry.
 * @returns The updated source registry validation model.
 */
export const sourceIntentNameSetter = (dataName: string, previousState: ISourceRegistryValidationModel, name: string, value: string, SOURCE_REGISTRY_ERRORS: ISourceRegistryErrorModel) => {
  if (dataName === SOURCE_REGISTRY_FORM_PARAM.sourceRegistryName) {
    return setFormElementStatus(previousState, name, value, SOURCE_REGISTRY_ERRORS.requiredSourceRegistryName, false, true);
  }
  return setFormElementStatus(previousState, name, value, '', true, true);
};

/**
 * @method formatJsonForInput
 * @description Gets the json formatted data
 * @returns json formatted data
 */
export const formatJsonForInput = (jsonData: string) => {
  let validJsonData;
  try {
    validJsonData = jsonData ? JSON.stringify(JSON.parse(jsonData), null, 1) : jsonData;
  } catch (e) {
    return jsonData;
  }
  return validJsonData;
};

/**
 * @method updateReplyTopicFormData
 * @description Updates the reply topic form data with the provided error and validation status.
 * @param previousData - The previous form data to be updated.
 * @param error - The error message to be set.
 * @param isValidStatus - The validation status to be set.
 * @returns The updated form data.
 */
export const updateReplyTopicFormData = (previousData: ISourceRegistryValidationModel, error: string, isValidStatus: boolean) => {
  previousData.replyToTopic.error = error;
  previousData.replyToTopic.isValid = isValidStatus;
  return previousData;
};

/**
 * @method isJson
 * @description Checks data is valid json or not
 * @param data: to be verify
 * @returns true if valid json else false
 */
export const isJson = (data: string): boolean => {
  if (typeof data !== 'string') return false;
  try {
      const result = JSON.parse(data); return typeof result === 'object' && result !== null;
  } catch (err) {
  return false;
}
};

/**
 * @method clearFormDataDynamicSource
 * @description This is function to dynamically clear selective fields in formData
 * @param formObj: this a formData object obtained from hook
 * @param fieldNames: array of formData field keys
 * @returns updated formData object
 */
export const clearFormDataDynamicSource = (formObj: ISourceRegistryValidationModel, fieldNames: string[]) => {
  fieldNames.forEach((element) => {
    formObj[element as keyof typeof formObj].value = '';
    formObj[element as keyof typeof formObj].error = '';
    formObj[element as keyof typeof formObj].isValid = true;
    formObj[element as keyof typeof formObj].isTouched = false;
  });
};

export const defaultSourceListStatus = `${STATUS_TYPES.ACTIVE}`;
export const defaultSourceListStatusArray: string[] = [
  STATUS_TYPES.ACTIVE
];

/**
 * @method getFilterParams
 * @description Gets the current filter parameters for Source Registry.
 * If the search url has params use those; otherwise fall back to Redux filter state.
 * @param search - The search url
 * @param filterState - The current filter state from Redux
 * @returns The filter parameters object
 */
export const getFilterParams = (search: string, filterState: ISourceRegistryFilterState): ISourceListQueryParams => {
  const params = new URLSearchParams(search);
  const parsedParams: ISourceListQueryParams = {
    searchText: params.get('searchText') ?? '',
    status: params.get('status') ?? '',
    fromDate: params.get('fromDate') ?? '',
    toDate: params.get('toDate') ?? '',
    sort: params.get('sort') ?? '',
    sortType: params.get('sortType') ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
  };

  const hasUrlFilter = parsedParams.searchText
    || parsedParams.status
    || parsedParams.fromDate
    || parsedParams.toDate
    || parsedParams.sort
    || parsedParams.sortType;

  if (hasUrlFilter) {
    return parsedParams;
  }

  return {
    searchText: filterState.searchText ?? '',
    status: filterState.status ?? '',
    fromDate: String(filterState.fromDate ?? ''),
    toDate: String(filterState.toDate ?? ''),
    sort: filterState.sort ?? '',
    sortType: filterState.sortType ?? '',
    page: params.get('page') ?? '',
    item: params.get('item') ?? '',
  };
};
